// src/security/permissions.js

/*
 * ShiftBridge Canonical Permission Contract
 *
 * PURPOSE
 * -------
 * This file is the single application-side authority for:
 *
 * 1. Platform role names
 * 2. Role aliases
 * 3. Default role permissions
 * 4. User/access-profile permission resolution
 * 5. Tenant access
 * 6. Facility access
 *
 * Firestore rules will be synchronized to the same contract in a later
 * package. Legacy permission files should delegate to this file rather
 * than defining their own role matrices.
 */

/* =========================================================
   PERMISSION KEYS
   ========================================================= */

export const PERMISSIONS = {
  CAN_VIEW_ALL_FACILITIES: "canViewAllFacilities",
  CAN_VIEW_ALL_TENANTS: "canViewAllTenants",

  CAN_MANAGE_USERS: "canManageUsers",
  CAN_VIEW_ADMIN_LOGS: "canViewAdminLogs",

  CAN_VIEW_RATES: "canViewRates",
  CAN_EDIT_RATES: "canEditRates",
  CAN_VIEW_FINANCIAL_PROJECTIONS: "canViewFinancialProjections",

   CAN_CREATE_SHIFTS: "canCreateShifts",
  CAN_COMPLETE_SHIFTS: "canCompleteShifts",

  CAN_ASSIGN_WORKERS: "canAssignWorkers",
  CAN_REASSIGN_WORKERS: "canReassignWorkers",
  CAN_AUTO_ASSIGN_SHIFTS: "canAutoAssignShifts",
  CAN_CANCEL_SHIFTS: "canCancelShifts",
  CAN_DELETE_SHIFTS: "canDeleteShifts",

  CAN_VIEW_INCIDENTS: "canViewIncidents",
  CAN_CREATE_INCIDENTS: "canCreateIncidents",
  CAN_RESOLVE_INCIDENTS: "canResolveIncidents",
  CAN_DELETE_INCIDENTS: "canDeleteIncidents",

  CAN_VIEW_COMMUNICATIONS: "canViewCommunications",
  CAN_SEND_COMMUNICATIONS: "canSendCommunications",
  CAN_DELETE_COMMUNICATIONS: "canDeleteCommunications",

  CAN_GENERATE_INVOICES: "canGenerateInvoices",
  CAN_VIEW_INVOICES: "canViewInvoices",
  CAN_VOID_INVOICES: "canVoidInvoices",
  CAN_MARK_INVOICES_PAID: "canMarkInvoicesPaid",

  CAN_VIEW_PAYROLL: "canViewPayroll",
  CAN_RUN_PAYROLL: "canRunPayroll",
  CAN_GENERATE_PAYROLL: "canGeneratePayroll",
  CAN_MANAGE_PAYROLL: "canManagePayroll",

  CAN_VIEW_FACILITY_PULSE: "canViewFacilityPulse",
};

/* =========================================================
   PLATFORM ROLES
   ========================================================= */

export const PLATFORM_ROLES = {
  OWNER_ADMIN: "owner_admin",
  FINANCE_ADMIN: "finance_admin",
  OPERATIONS_ADMIN: "operations_admin",

  /*
   * Internal enterprise administration roles.
   */
  HR_ADMIN: "hr_admin",
  CREDENTIALING_ADMIN: "credentialing_admin",

  AGENCY_ADMIN: "agency_admin",

  FACILITY_PLANNER: "facility_planner",

  FACILITY_CLIENT: "facility_client",
  FACILITY_ADMIN_VIEWER: "facility_admin_viewer",

  WORKER: "worker",
};

/* =========================================================
   NORMALIZATION
   ========================================================= */

export function normalizeId(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

const ROLE_ALIASES = {
  owner_admin: PLATFORM_ROLES.OWNER_ADMIN,
  owner: PLATFORM_ROLES.OWNER_ADMIN,
  admin: PLATFORM_ROLES.OWNER_ADMIN,

  finance_admin: PLATFORM_ROLES.FINANCE_ADMIN,
  finance: PLATFORM_ROLES.FINANCE_ADMIN,

  operations_admin: PLATFORM_ROLES.OPERATIONS_ADMIN,
  operations: PLATFORM_ROLES.OPERATIONS_ADMIN,
  ops_admin: PLATFORM_ROLES.OPERATIONS_ADMIN,

  hr_admin: PLATFORM_ROLES.HR_ADMIN,
  hr: PLATFORM_ROLES.HR_ADMIN,

  credentialing_admin:
    PLATFORM_ROLES.CREDENTIALING_ADMIN,

  credentialing:
    PLATFORM_ROLES.CREDENTIALING_ADMIN,

  agency_admin: PLATFORM_ROLES.AGENCY_ADMIN,

  facility_planner: PLATFORM_ROLES.FACILITY_PLANNER,
  facilityplanner: PLATFORM_ROLES.FACILITY_PLANNER,
  planner: PLATFORM_ROLES.FACILITY_PLANNER,
  facility: PLATFORM_ROLES.FACILITY_PLANNER,

  facility_client: PLATFORM_ROLES.FACILITY_CLIENT,

  facility_admin_viewer:
    PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,

  facility_admin:
    PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,

  /*
   * Canonical worker identity.
   *
   * Older ShiftBridge worker records may still carry
   * historical role values such as "nurse" or "caregiver".
   * They are operational workers and normalize to WORKER.
   */
  worker: PLATFORM_ROLES.WORKER,
  nurse: PLATFORM_ROLES.WORKER,
  caregiver: PLATFORM_ROLES.WORKER,
};

export function normalizePlatformRole(value) {
  const normalized = normalizeId(value);

  if (!normalized) {
    return "";
  }

  return ROLE_ALIASES[normalized] || normalized;
}

/* =========================================================
   DEFAULT ROLE PERMISSIONS
   ========================================================= */

const NO_ACCESS = {
  [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]: false,
  [PERMISSIONS.CAN_VIEW_ALL_TENANTS]: false,

  [PERMISSIONS.CAN_MANAGE_USERS]: false,
  [PERMISSIONS.CAN_VIEW_ADMIN_LOGS]: false,

  [PERMISSIONS.CAN_VIEW_RATES]: false,
  [PERMISSIONS.CAN_EDIT_RATES]: false,
  [PERMISSIONS.CAN_VIEW_FINANCIAL_PROJECTIONS]: false,

  [PERMISSIONS.CAN_CREATE_SHIFTS]: false,
  [PERMISSIONS.CAN_COMPLETE_SHIFTS]: false,

  [PERMISSIONS.CAN_ASSIGN_WORKERS]: false,
  [PERMISSIONS.CAN_REASSIGN_WORKERS]: false,
  [PERMISSIONS.CAN_AUTO_ASSIGN_SHIFTS]: false,
  [PERMISSIONS.CAN_CANCEL_SHIFTS]: false,
  [PERMISSIONS.CAN_DELETE_SHIFTS]: false,

  [PERMISSIONS.CAN_VIEW_INCIDENTS]: false,
  [PERMISSIONS.CAN_CREATE_INCIDENTS]: false,
  [PERMISSIONS.CAN_RESOLVE_INCIDENTS]: false,
  [PERMISSIONS.CAN_DELETE_INCIDENTS]: false,

  [PERMISSIONS.CAN_VIEW_COMMUNICATIONS]: false,
  [PERMISSIONS.CAN_SEND_COMMUNICATIONS]: false,
  [PERMISSIONS.CAN_DELETE_COMMUNICATIONS]: false,

  [PERMISSIONS.CAN_GENERATE_INVOICES]: false,
  [PERMISSIONS.CAN_VIEW_INVOICES]: false,
  [PERMISSIONS.CAN_VOID_INVOICES]: false,
  [PERMISSIONS.CAN_MARK_INVOICES_PAID]: false,

  [PERMISSIONS.CAN_VIEW_PAYROLL]: false,
  [PERMISSIONS.CAN_RUN_PAYROLL]: false,
  [PERMISSIONS.CAN_GENERATE_PAYROLL]: false,
  [PERMISSIONS.CAN_MANAGE_PAYROLL]: false,

  [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: false,
};

function permissionSet(overrides = {}) {
  return {
    ...NO_ACCESS,
    ...overrides,
  };
}

export const ROLE_PERMISSIONS = {
  [PLATFORM_ROLES.OWNER_ADMIN]: permissionSet({
    [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]: true,
    [PERMISSIONS.CAN_VIEW_ALL_TENANTS]: true,

    [PERMISSIONS.CAN_MANAGE_USERS]: true,
    [PERMISSIONS.CAN_VIEW_ADMIN_LOGS]: true,

    [PERMISSIONS.CAN_VIEW_RATES]: true,
    [PERMISSIONS.CAN_EDIT_RATES]: true,
    [PERMISSIONS.CAN_VIEW_FINANCIAL_PROJECTIONS]: true,

    [PERMISSIONS.CAN_CREATE_SHIFTS]: true,
    [PERMISSIONS.CAN_COMPLETE_SHIFTS]: true,
    [PERMISSIONS.CAN_ASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_REASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_AUTO_ASSIGN_SHIFTS]: true,
    [PERMISSIONS.CAN_CANCEL_SHIFTS]: true,
    [PERMISSIONS.CAN_DELETE_SHIFTS]: true,

    [PERMISSIONS.CAN_VIEW_INCIDENTS]: true,
    [PERMISSIONS.CAN_CREATE_INCIDENTS]: true,
    [PERMISSIONS.CAN_RESOLVE_INCIDENTS]: true,
    [PERMISSIONS.CAN_DELETE_INCIDENTS]: true,

    [PERMISSIONS.CAN_VIEW_COMMUNICATIONS]: true,
    [PERMISSIONS.CAN_SEND_COMMUNICATIONS]: true,
    [PERMISSIONS.CAN_DELETE_COMMUNICATIONS]: true,

    [PERMISSIONS.CAN_GENERATE_INVOICES]: true,
    [PERMISSIONS.CAN_VIEW_INVOICES]: true,
    [PERMISSIONS.CAN_VOID_INVOICES]: true,
    [PERMISSIONS.CAN_MARK_INVOICES_PAID]: true,

    [PERMISSIONS.CAN_VIEW_PAYROLL]: true,
    [PERMISSIONS.CAN_RUN_PAYROLL]: true,
    [PERMISSIONS.CAN_GENERATE_PAYROLL]: true,
    [PERMISSIONS.CAN_MANAGE_PAYROLL]: true,

    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),

  [PLATFORM_ROLES.FINANCE_ADMIN]: permissionSet({
    [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]: true,

    [PERMISSIONS.CAN_VIEW_ADMIN_LOGS]: true,

    [PERMISSIONS.CAN_VIEW_RATES]: true,
    [PERMISSIONS.CAN_VIEW_FINANCIAL_PROJECTIONS]: true,

    [PERMISSIONS.CAN_GENERATE_INVOICES]: true,
    [PERMISSIONS.CAN_VIEW_INVOICES]: true,
    [PERMISSIONS.CAN_VOID_INVOICES]: true,
    [PERMISSIONS.CAN_MARK_INVOICES_PAID]: true,

    [PERMISSIONS.CAN_VIEW_PAYROLL]: true,

    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),

  [PLATFORM_ROLES.OPERATIONS_ADMIN]: permissionSet({
    [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]: true,

    /*
     * This intentionally follows the existing primary ShiftBridge
     * permission contract in src/security/permissions.js.
     */
    [PERMISSIONS.CAN_MANAGE_USERS]: true,
    [PERMISSIONS.CAN_VIEW_ADMIN_LOGS]: true,

    [PERMISSIONS.CAN_CREATE_SHIFTS]: true,
    [PERMISSIONS.CAN_COMPLETE_SHIFTS]: true,
    [PERMISSIONS.CAN_ASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_REASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_AUTO_ASSIGN_SHIFTS]: true,
    [PERMISSIONS.CAN_CANCEL_SHIFTS]: true,
    [PERMISSIONS.CAN_DELETE_SHIFTS]: true,

    [PERMISSIONS.CAN_VIEW_INCIDENTS]: true,
    [PERMISSIONS.CAN_CREATE_INCIDENTS]: true,
    [PERMISSIONS.CAN_RESOLVE_INCIDENTS]: true,

    [PERMISSIONS.CAN_VIEW_COMMUNICATIONS]: true,
    [PERMISSIONS.CAN_SEND_COMMUNICATIONS]: true,

    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),

    /*
   * =======================================================
   * HR ADMIN
   * =======================================================
   *
   * Human-resources administration is primarily concerned
   * with workforce, onboarding, employee records, incidents
   * and operational communication.
   *
   * Facility visibility may later be narrowed by an
   * accessProfiles/{uid}.permissions override when the
   * administrator is assigned selected facilities only.
   */

  [PLATFORM_ROLES.HR_ADMIN]: permissionSet({
    [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]: true,

    [PERMISSIONS.CAN_MANAGE_USERS]: true,
    [PERMISSIONS.CAN_VIEW_ADMIN_LOGS]: true,

    [PERMISSIONS.CAN_VIEW_INCIDENTS]: true,
    [PERMISSIONS.CAN_CREATE_INCIDENTS]: true,
    [PERMISSIONS.CAN_RESOLVE_INCIDENTS]: true,

    [PERMISSIONS.CAN_VIEW_COMMUNICATIONS]: true,
    [PERMISSIONS.CAN_SEND_COMMUNICATIONS]: true,

    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),


  /*
   * =======================================================
   * CREDENTIALING ADMIN
   * =======================================================
   *
   * Credentialing administrators manage worker records,
   * onboarding/compliance review and facility workforce
   * eligibility, but do not receive shift-operation or
   * financial authority by default.
   */

  [PLATFORM_ROLES.CREDENTIALING_ADMIN]:
    permissionSet({
      [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]: true,

      [PERMISSIONS.CAN_MANAGE_USERS]: true,
      [PERMISSIONS.CAN_VIEW_ADMIN_LOGS]: true,

      [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
    }),

  [PLATFORM_ROLES.AGENCY_ADMIN]: permissionSet({
    [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]: true,

    [PERMISSIONS.CAN_MANAGE_USERS]: true,
    [PERMISSIONS.CAN_VIEW_ADMIN_LOGS]: true,

    [PERMISSIONS.CAN_CREATE_SHIFTS]: true,
    [PERMISSIONS.CAN_COMPLETE_SHIFTS]: true,
    [PERMISSIONS.CAN_ASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_REASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_AUTO_ASSIGN_SHIFTS]: true,
    [PERMISSIONS.CAN_CANCEL_SHIFTS]: true,
    [PERMISSIONS.CAN_DELETE_SHIFTS]: true,

    [PERMISSIONS.CAN_VIEW_INCIDENTS]: true,
    [PERMISSIONS.CAN_CREATE_INCIDENTS]: true,
    [PERMISSIONS.CAN_RESOLVE_INCIDENTS]: true,

    [PERMISSIONS.CAN_VIEW_COMMUNICATIONS]: true,
    [PERMISSIONS.CAN_SEND_COMMUNICATIONS]: true,

    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),

  [PLATFORM_ROLES.FACILITY_PLANNER]: permissionSet({
    /*
     * Core Planner shift operations.
     */
    [PERMISSIONS.CAN_CREATE_SHIFTS]: true,
    [PERMISSIONS.CAN_COMPLETE_SHIFTS]: true,

    [PERMISSIONS.CAN_ASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_REASSIGN_WORKERS]: true,
    [PERMISSIONS.CAN_CANCEL_SHIFTS]: true,

    /*
     * Planner operational visibility.
     *
     * Delete permissions intentionally remain false.
     */
    [PERMISSIONS.CAN_VIEW_INCIDENTS]: true,
    [PERMISSIONS.CAN_CREATE_INCIDENTS]: true,
    [PERMISSIONS.CAN_RESOLVE_INCIDENTS]: true,

    [PERMISSIONS.CAN_VIEW_COMMUNICATIONS]: true,
    [PERMISSIONS.CAN_SEND_COMMUNICATIONS]: true,

    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),

  [PLATFORM_ROLES.FACILITY_CLIENT]: permissionSet({
    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),

  [PLATFORM_ROLES.FACILITY_ADMIN_VIEWER]: permissionSet({
    [PERMISSIONS.CAN_VIEW_FACILITY_PULSE]: true,
  }),

  [PLATFORM_ROLES.WORKER]: permissionSet({}),
};

/* =========================================================
   ACCESS PROFILE RESOLUTION
   ========================================================= */

/*
 * During migration some screens have only users/{uid}, while newer
 * bootstrap code will attach accessProfiles/{uid}.
 *
 * These names allow the application to migrate without introducing
 * another competing permission engine.
 */
export function getAttachedAccessProfile(user = {}) {
  const candidates = [
    user?.accessProfile,
    user?.operationalAccess,
    user?._accessProfile,
  ];

  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate)
    ) {
      return candidate;
    }
  }

  return null;
}

/*
 * =========================================================
 * ACCESS PROFILE STATE
 * =========================================================
 *
 * Canonical interpretation of operational access state.
 *
 * users/{uid} remains identity/profile data.
 * accessProfiles/{uid}, when attached, is the operational
 * authorization record.
 */

export function getAccessProfileState(user = {}) {
  const accessProfile =
    getAttachedAccessProfile(user);

  const hasAccessProfile =
    Boolean(accessProfile);

  const isAccessEnabled =
    !hasAccessProfile ||
    (
      accessProfile?.active !== false &&
      accessProfile?.disabled !== true
    );

  return {
    accessProfile,
    hasAccessProfile,
    isAccessEnabled,
  };
}

export function getUserRole(user = {}) {
  const accessProfile =
    getAttachedAccessProfile(user);

  /*
   * Once an operational access profile exists,
   * it is authoritative for operational role.
   *
   * Do not silently borrow role identity from
   * users/{uid} when accessProfiles/{uid} exists.
   * An incomplete access profile must surface as
   * a provisioning defect rather than creating
   * different role interpretations across modules.
   */
  if (accessProfile) {
    return normalizePlatformRole(
      accessProfile?.platformRole ||
        accessProfile?.role ||
        ""
    );
  }

  /*
   * Legacy / pre-operational account fallback.
   */
  return normalizePlatformRole(
    user?.platformRole ||
      user?.role ||
      ""
  );
}

export function getRolePermissions(role) {
  const normalizedRole =
    normalizePlatformRole(role);

  return {
    ...(ROLE_PERMISSIONS[normalizedRole] ||
      NO_ACCESS),
  };
}

export function getUserPermissions(user = {}) {
  const {
    accessProfile,
    hasAccessProfile,
    isAccessEnabled,
  } = getAccessProfileState(user);

  /*
   * An attached but disabled operational access profile
   * must not retain permissions from either its role or
   * the legacy users/{uid} document.
   */
  if (
    hasAccessProfile &&
    !isAccessEnabled
  ) {
    return {
      ...NO_ACCESS,
    };
  }

  const role = getUserRole(user);

  const rolePermissions =
    getRolePermissions(role);

  /*
   * Once accessProfiles/{uid} exists, it is the
   * authoritative source for explicit operational
   * permission overrides.
   *
   * Do not merge users/{uid}.permissions into an
   * operational account that already has an access
   * profile.
   */
  if (hasAccessProfile) {
    const accessPermissions =
      accessProfile?.permissions &&
      typeof accessProfile.permissions === "object" &&
      !Array.isArray(
        accessProfile.permissions
      )
        ? accessProfile.permissions
        : {};

    return {
      ...rolePermissions,
      ...accessPermissions,
    };
  }

  /*
   * Legacy / pre-operational accounts without an
   * access profile continue to use users/{uid}
   * permission overrides during migration.
   */
  const userPermissions =
    user?.permissions &&
    typeof user.permissions === "object" &&
    !Array.isArray(user.permissions)
      ? user.permissions
      : {};

  return {
    ...rolePermissions,
    ...userPermissions,
  };
}

export function hasPermission(
  user,
  permissionKey
) {
  if (!user || !permissionKey) {
    return false;
  }

  return Boolean(
    getUserPermissions(user)[permissionKey]
  );
}

export function hasAnyPermission(
  user,
  permissionKeys = []
) {
  return permissionKeys.some((key) =>
    hasPermission(user, key)
  );
}

export function hasAllPermissions(
  user,
  permissionKeys = []
) {
  return permissionKeys.every((key) =>
    hasPermission(user, key)
  );
}

/* =========================================================
   TENANT SCOPE
   ========================================================= */

export function getTenantId(user = {}) {
  const accessProfile =
    getAttachedAccessProfile(user);

  /*
   * Once an operational access profile exists,
   * it is authoritative for tenant authorization.
   *
   * Do not silently fall back to users/{uid} when
   * the access profile is incomplete. That would
   * hide a provisioning defect and create a
   * different tenant interpretation between the
   * application and Firestore rules.
   */
  if (accessProfile) {
    return normalizeId(
      accessProfile?.tenantId || ""
    );
  }

  /*
   * Legacy / pre-operational accounts may not yet
   * have accessProfiles/{uid}.
   *
   * users/{uid}.tenantId remains the canonical
   * fallback during migration. tenantKey is kept
   * only as legacy compatibility.
   *
   * tenantName is intentionally NOT an ID.
   */
  return normalizeId(
    user?.tenantId ||
      user?.tenantKey ||
      ""
  );
}

export function canAccessTenant(
  user = {},
  tenantId = ""
) {
  const targetTenantId =
    normalizeId(tenantId);

  if (!targetTenantId) {
    return false;
  }

  // This marker is resolved from protected platformAdmins/{uid} during bootstrap.
  // Firestore independently verifies the trusted record on every operation.
  if (user.trustedPlatformAdmin === true) return true;
  if (user.active === false || user.disabled === true || targetTenantId === "all") return false;

  return (
    getTenantId(user) === targetTenantId
  );
}

/* =========================================================
   FACILITY SCOPE
   ========================================================= */

function normalizeIdList(values = []) {
  return Array.from(
    new Set(
      values
        .map(normalizeId)
        .filter(Boolean)
        .filter((value) => value !== "all")
    )
  );
}

export function getFacilityIds(user = {}) {
  const accessProfile =
    getAttachedAccessProfile(user);

  /*
   * Once an operational access profile exists,
   * it is authoritative for facility authorization.
   *
   * Do not combine accessProfiles/{uid} facilities
   * with users/{uid} facilities. Mixing the two can
   * hide incomplete provisioning and cause the UI
   * and Firestore rules to resolve different scopes.
   */
  if (accessProfile) {
    return normalizeIdList([
      accessProfile?.facilityId,
      accessProfile?.primaryFacilityId,

      ...(Array.isArray(
        accessProfile?.facilityIds
      )
        ? accessProfile.facilityIds
        : []),

      ...(Array.isArray(
        accessProfile?.allowedFacilityIds
      )
        ? accessProfile.allowedFacilityIds
        : []),

      ...(Array.isArray(
        accessProfile?.assignedFacilityIds
      )
        ? accessProfile.assignedFacilityIds
        : []),
    ]);
  }

  /*
   * Legacy / pre-operational accounts without an
   * access profile continue to resolve facility
   * membership from users/{uid}.
   *
   * This is important for workers who may exist
   * before facility assignment is finalized.
   */
  return normalizeIdList([
    user?.facilityId,
    user?.primaryFacilityId,

    ...(Array.isArray(user?.facilityIds)
      ? user.facilityIds
      : []),

    ...(Array.isArray(
      user?.allowedFacilityIds
    )
      ? user.allowedFacilityIds
      : []),

    ...(Array.isArray(
      user?.assignedFacilityIds
    )
      ? user.assignedFacilityIds
      : []),
  ]);
}

export function getPrimaryFacilityId(
  user = {}
) {
  const accessProfile =
    getAttachedAccessProfile(user);

  /*
   * Once an operational access profile exists,
   * it is authoritative for primary facility scope.
   *
   * Do not silently borrow primary facility identity
   * from users/{uid} when the access profile exists.
   */
  if (accessProfile) {
    const explicit = normalizeId(
      accessProfile?.primaryFacilityId ||
        accessProfile?.facilityId ||
        ""
    );

    if (explicit) {
      return explicit;
    }

    /*
     * If no explicit primary facility exists,
     * fall back only to facilities contained in the
     * authoritative access profile.
     *
     * getFacilityIds() already enforces that rule.
     */
    return getFacilityIds(user)[0] || "";
  }

  /*
   * Legacy / pre-operational account fallback.
   */
  const explicit = normalizeId(
    user?.primaryFacilityId ||
      user?.facilityId ||
      ""
  );

  if (explicit) {
    return explicit;
  }

  return getFacilityIds(user)[0] || "";
}

export function canAccessFacility(
  user = {},
  facilityId = "",
  tenantId = ""
) {
  if (!user) {
    return false;
  }

  const targetFacilityId =
    normalizeId(facilityId);

  if (!targetFacilityId) {
    return false;
  }

  const targetTenantId =
    normalizeId(tenantId);

  if (
    targetTenantId &&
    !canAccessTenant(user, targetTenantId)
  ) {
    return false;
  }

  const role = getUserRole(user);

  if (
    role === PLATFORM_ROLES.OWNER_ADMIN ||
    hasPermission(
      user,
      PERMISSIONS.CAN_VIEW_ALL_FACILITIES
    )
  ) {
    return true;
  }

  return getFacilityIds(user).includes(
    targetFacilityId
  );
}

export function filterByFacilityScope(
  user,
  items = []
) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter((item) => {
    const facilityId =
      item?.facilityId ||
      item?.facilityID ||
      item?.facility ||
      "";

    const tenantId =
      item?.tenantId ||
      item?.tenantKey ||
      "";

    return canAccessFacility(
      user,
      facilityId,
      tenantId
    );
  });
}

/* =========================================================
   ROLE HELPERS
   ========================================================= */

export function isOwnerAdmin(user = {}) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.OWNER_ADMIN
  );
}

export function isFinanceAdmin(user = {}) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.FINANCE_ADMIN
  );
}

export function isOperationsAdmin(user = {}) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.OPERATIONS_ADMIN
  );
}

export function isHrAdmin(user = {}) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.HR_ADMIN
  );
}

export function isCredentialingAdmin(
  user = {}
) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.CREDENTIALING_ADMIN
  );
}

export function isAgencyAdmin(user = {}) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.AGENCY_ADMIN
  );
}

/*
 * =========================================================
 * PLATFORM ADMIN FAMILY
 * =========================================================
 *
 * Platform administrators operate at the agency /
 * enterprise level, but their individual capabilities
 * still come from ROLE_PERMISSIONS.
 *
 * Do not use isAgencyAdmin() as a generic admin test.
 */
export function isPlatformAdmin(user = {}) {
  const role = getUserRole(user);

  return [
    PLATFORM_ROLES.OWNER_ADMIN,
    PLATFORM_ROLES.FINANCE_ADMIN,
    PLATFORM_ROLES.OPERATIONS_ADMIN,
    PLATFORM_ROLES.HR_ADMIN,
    PLATFORM_ROLES.CREDENTIALING_ADMIN,
    PLATFORM_ROLES.AGENCY_ADMIN,
  ].includes(role);
}

export function isFacilityPlanner(
  user = {}
) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.FACILITY_PLANNER
  );
}

export function isFacilityClient(
  user = {}
) {
  const role = getUserRole(user);

  return [
    PLATFORM_ROLES.FACILITY_CLIENT,
    PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
  ].includes(role);
}

export function isWorker(user = {}) {
  return (
    getUserRole(user) ===
    PLATFORM_ROLES.WORKER
  );
}

/* =========================================================
   COMPATIBILITY HELPERS
   ========================================================= */

export function requirePermission(
  user,
  permissionKey
) {
  return hasPermission(
    user,
    permissionKey
  );
}

export function getDeniedMessage(
  action = "perform this action"
) {
  return `You do not have permission to ${action}.`;
}

export const DESTRUCTIVE_PERMISSIONS = {
  DELETE_SHIFT:
    PERMISSIONS.CAN_DELETE_SHIFTS,

  CANCEL_SHIFT:
    PERMISSIONS.CAN_CANCEL_SHIFTS,

  VOID_INVOICE:
    PERMISSIONS.CAN_VOID_INVOICES,

  MARK_INVOICE_PAID:
    PERMISSIONS.CAN_MARK_INVOICES_PAID,

  MANAGE_USERS:
    PERMISSIONS.CAN_MANAGE_USERS,

  EDIT_RATES:
    PERMISSIONS.CAN_EDIT_RATES,
};